// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import { Chip } from '@mui/material';

/**
 * SolyticsChip - A comprehensive chip component built on MUI Chip
 *
 * @component
 * @description
 * SolyticsChip is a wrapper around Material-UI's Chip component that extends its functionality
 * with custom shape variants and a new 'bordered' variant while maintaining controlled prop exposure.
 *

 *
* @param {string} id - HTML id attribute
* @param {string} title - Tooltip text
 * @param {string} label - The content of the component
 * @param {string} variant - The variant to use: 'filled' | 'outlined' | 'bordered' | 'text'
 * @param {string} color - The color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'high' | 'medium' | 'low'
 * @param {string} size - The size: 'small' | 'medium'
 * @param {string} shape - Custom shape: 'rounded' | 'sharp' | 'pill' | 'circle'
 * @param {boolean} disabled - If true, the component is disabled
* @param {function} onDelete - Delete handler (shows delete icon)
* @param {function} onClick - Click handler (makes chip clickable)
* @param {boolean} clickable - If true, chip is clickable (defaults to true if onClick is provided)
* @param {React.ReactNode} icon - Icon element (shows before label)
* @param {React.ReactNode} startIcon - Icon at the start (custom prop)
* @param {string|number} minWidth - Minimum width
* @param {string|number} maxWidth - Maximum width
* @param {string|number} minHeight - Minimum height
* @param {string|number} maxHeight - Maximum height
 *
 * @returns {React.ReactElement} A chip element
 */

// Default MUI Chip props configuration
const DEFAULT_CHIP_PROPS = {
  size: 'small',
  variant: 'filled',
  clickable: false,
  disabled: false,
};
const DELETE_ICON_STYLE = {
  cursor: 'pointer',
  marginRight: '8px',
};

// Color map helper for all color logic
const getChipColorMap = (theme) => ({
  high: {
    color: '#F04438',
    backgroundColor: '#FEE4E2',
    borderColor: '#F04438',
  },
  error: {
    color: '#F04438',
    backgroundColor: '#FEE4E2',
    borderColor: '#F04438',
  },
  medium: {
    color: '#0C3270',
    backgroundColor: '#E5EFFF',
    borderColor: '#0C3270',
  },
  primary: {
    color: '#0C3270',
    backgroundColor: '#E5EFFF',
    borderColor: '#0C3270',
  },
  info: {
    color: '#0C3270',
    backgroundColor: '#E5EFFF',
    borderColor: '#0C3270',
  },
  low: {
    color: '#05603A',
    backgroundColor: '#D1FADF',
    borderColor: '#05603A',
  },
  success: {
    color: '#05603A',
    backgroundColor: '#D1FADF',
    borderColor: '#05603A',
  },
  warning: {
    color: '#B54708',
    backgroundColor: '#FEF0C7',
    borderColor: '#B54708',
  },
  secondary: {
    color: theme.palette?.text?.secondary || '#71717A',
    backgroundColor: theme.palette?.grey?.[20] || '#F4F4F4',
    borderColor: theme.palette?.grey?.[30] || '#71717A',
  },
});

const SolyticsChip = ({
  id,
  title,
  label,
  variant = 'filled',
  color = 'primary',
  size = 'small',
  shape = 'rounded',
  disabled = false,
  onDelete,
  onClick,
  icon,
  startIcon,
  clickable,
  // deleteIcon,
  // iconStyle = {},
  // deleteIconStyle = {},
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
}) => {
  /**
   * Get variant-specific styles based on variant and color
   */
  const getVariantStyles = (theme) => {
    const colorMap = getChipColorMap(theme);
    const selectedColor = colorMap[color] || colorMap.primary;

    const variantStyleMap = {
      filled: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        ...selectedColor,
        '&:hover': {
          backgroundColor: selectedColor.backgroundColor,
          opacity: 0.9,
        },
        '& .MuiChip-deleteIcon': {
          color: selectedColor.color,
          '&:hover': {
            color: selectedColor.color,
            opacity: 0.7,
          },
        },
        '& .MuiChip-icon': {
          color: selectedColor.color,
        },
        '& .MuiChip-label': {
          color: selectedColor.color,
        },
      },
      outlined: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        backgroundColor: 'transparent',
      },
      bordered: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        ...selectedColor,
        border: `1px solid ${selectedColor.borderColor}`,
        '&:hover': {
          backgroundColor: selectedColor.backgroundColor,
          opacity: 0.9,
        },
        '& .MuiChip-deleteIcon': {
          color: selectedColor.color,
          '&:hover': {
            color: selectedColor.color,
            opacity: 0.7,
          },
        },
        '& .MuiChip-icon': {
          color: selectedColor.color,
        },
        '& .MuiChip-label': {
          color: selectedColor.color,
        },
      },
      text: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        backgroundColor: 'transparent',
        border: 'none',
        color: selectedColor.color,
        '&:hover': {
          backgroundColor: theme.palette.action?.hover || 'rgba(0, 0, 0, 0.04)',
        },
        '& .MuiChip-deleteIcon': {
          color: selectedColor.color,
          '&:hover': {
            color: selectedColor.color,
            opacity: 0.7,
          },
        },
        '& .MuiChip-icon': {
          color: selectedColor.color,
        },
        '& .MuiChip-label': {
          color: selectedColor.color,
        },
      },
    };
    return variantStyleMap[variant] || variantStyleMap.filled;
  };

  /**
   * Get shape-specific styles
   */
  const getShapeStyles = () => {
    const shapeStyleMap = {
      rounded: {
        borderRadius: '6px',
      },
      sharp: {
        borderRadius: '2px',
      },
      pill: {
        borderRadius: '16px',
      },
      circle: {
        borderRadius: '50%',
        minWidth: '32px',
        minHeight: '32px',
        width: '32px',
        height: '32px',
        '& .MuiChip-label': {
          display: 'none ', // Hide label for circle shape - icon only
        },
        '& .MuiChip-icon': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiChip-icon svg': {
          display: 'block',
          margin: 'auto',
        },
      },
    };

    return shapeStyleMap[shape] || shapeStyleMap.rounded;
  };

  /**
   * Determine which icon to use (priority: startIcon > icon)
   */
  const getIconProp = () => {
    if (startIcon) return startIcon;
    if (icon) return icon;
    return undefined;
  };

  /**
   * Build chip props object
   */
  // Determine if chip should be clickable
  const isClickable = clickable !== undefined ? clickable : Boolean(onClick);

  const chipProps = {
    ...DEFAULT_CHIP_PROPS,
    // For circle shape, don't pass label (icon-only)
    ...(shape !== 'circle' && { label }),
    ...(variant !== 'bordered' && variant !== 'text' && { variant }),
    ...(variant !== 'bordered' && variant !== 'text' && { color }),
    size,
    disabled,
    clickable: isClickable,
    ...(onClick && { onClick }),
    ...(onDelete && { onDelete }),
    ...(getIconProp() && { icon: getIconProp() }),
    ...(id && { id }),
    ...(title && { title }),
    sx: (theme) => {
      const colorMap = getChipColorMap(theme);
      const selectedColor = colorMap[color] || colorMap.primary;

      return {
        ...getVariantStyles(theme),
        ...getShapeStyles(),
        ...(minWidth && { minWidth }),
        ...(maxWidth && { maxWidth }),
        ...(minHeight && { minHeight }),
        ...(maxHeight && { maxHeight }),

        '&&': {
          color: selectedColor.color,

          '& .MuiChip-label': {
            color: selectedColor.color,
            ...(shape !== 'circle' && {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
          },

          '& .MuiChip-icon': {
            color: selectedColor.color,
            ...(shape === 'circle' && {
              margin: '0px',
              padding: '0px',
            }),
          },

          '& .MuiChip-deleteIcon': {
            ...DELETE_ICON_STYLE,
            color: selectedColor.color,
            '&:hover': {
              color: selectedColor.color,
              opacity: 0.7,
            },
          },
        },

        '&:focus-visible': {
          outline: `2px solid ${theme.palette?.primary?.main || '#0C3270'}`,
          outlineOffset: '2px',
        },

        ...(isClickable &&
          !disabled && {
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            '&:active': {
              opacity: 0.9,
            },
          }),
      };
    },
  };

  // Handle bordered variant by using filled as base
  if (variant === 'bordered') {
    chipProps.variant = 'filled';
  }
  // Handle text variant by using filled as base (MUI doesn't have text variant for Chip)
  if (variant === 'text') {
    chipProps.variant = 'filled';
  }

  return <Chip {...chipProps} />;
};

export default SolyticsChip;

/**
 * * ## Features
 * - Full support for MUI Chip props (variant, color, size, etc.)
 * - Custom shape variants: rounded, sharp, pill, circle
 * - New 'bordered' variant with light-toned backgrounds
 * - Support for icon with custom styling
 * - Theme integration with Figma design tokens
 * - onDelete support with custom styling
 * - Min-width and max-width control
 * - Accessibility compliant (WCAG 2.1 AA)
 *
 * ## Variants
 * - `filled`: Solid background (MUI default)
 * - `outlined`: Border with transparent background (MUI default)
 * - `bordered`: Light-toned background with matching text and border colors
 * - `text`: No background or border, colored text only (similar to text button variant)
 *
 * ## Shape Variants
 * - `rounded`: Default rounded chip (borderRadius: 6px)
 * - `sharp`: Sharp corners (borderRadius: 2px)
 * - `pill`: Fully rounded (borderRadius: 16px)
 * - `circle`: Perfect circle shape (borderRadius: 50%, fixed 32x32px). Ideal for icon-only chips. Label is hidden when using circle shape.
 *
 * ## Color Severity Levels (based on Figma)
 * - `high`: Red tones (#F04438 text, #FEE4E2 bg for bordered)
 * - `medium`: Blue tones (#0C3270 text, #E5EFFF bg for bordered)
 * - `low`: Green tones (#05603A text, #D1FADF bg for bordered)
 *
 * ## Examples
 *
 * ```jsx
 * // Basic filled chip
 * <SolyticsChip label="Failed" color="error" />
 *
 * // Bordered variant chips
 * <SolyticsChip label="Initial Review" variant="bordered" color="info" />
 * <SolyticsChip label="Success" variant="bordered" color="success" />
 *
 * // With delete handler
 * <SolyticsChip
 *   label="Removable"
 *   variant="bordered"
 *   color="primary"
 *   onDelete={() => handleDelete()}
 * />
 *
 * // With icon
 * <SolyticsChip
 *   label="Document"
 *   icon={<DocumentIcon />}
 *   variant="bordered"
 *   color="warning"
 * />
 *
 * // Custom shape
 * <SolyticsChip
 *   label="Pill Chip"
 *   shape="pill"
 *   variant="bordered"
 *   color="success"
 * />
 *
 * // Circle shape (icon-only)
 * <SolyticsChip
 *   label="Icon"
 *   shape="circle"
 *   variant="filled"
 *   color="primary"
 *   icon={<CheckIcon />}
 * />
 *
 * // With size constraints
 * <SolyticsChip
 *   label="Constrained"
 *   minWidth="100px"
 *   maxWidth="200px"
 *   variant="filled"
 * />
 * ```
 * */
